import React from 'react';

/**
 * Top-level error boundary. Without one, any render/chunk-load exception unmounts
 * the entire React tree and the user is left staring at a blank white page with
 * no way forward. This catches it and offers a reload instead.
 *
 * Chunk-load errors (stale cache after a deploy) trigger an automatic one-time
 * reload; anything else shows a recoverable fallback with a manual reload.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    const isChunkError =
      error && (error.name === 'ChunkLoadError' ||
        /Loading chunk [\d]+ failed/i.test(error.message || '') ||
        /Failed to fetch dynamically imported module/i.test(error.message || ''));
    return { hasError: true, isChunkError };
  }

  componentDidCatch(error) {
    // A stale-cache chunk error is recoverable by fetching the fresh build once.
    if (this.state.isChunkError) {
      const KEY = 'benta_boundary_reloaded_at';
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
      }
    }
    // eslint-disable-next-line no-console
    console.error('App error boundary caught:', error);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 12, fontFamily: 'system-ui, sans-serif', padding: 24, textAlign: 'center',
      }}>
        <div style={{ fontSize: 18, fontWeight: 600 }}>
          {this.state.isChunkError ? 'Updating to the latest version…' : 'Something went wrong'}
        </div>
        <div style={{ opacity: 0.7, maxWidth: 420 }}>
          {this.state.isChunkError
            ? 'A new version was released. Reloading to pick it up.'
            : 'This screen hit an unexpected error. Reloading usually fixes it.'}
        </div>
        <button
          onClick={() => window.location.reload()}
          style={{ padding: '8px 20px', borderRadius: 8, border: 0, background: '#29a38f', color: '#fff', cursor: 'pointer', fontSize: 14 }}
        >
          Reload
        </button>
      </div>
    );
  }
}
